import { useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { FormAutocompleteField } from '@/components/Form/FormAutocompleteField/FormAutocompleteField';
import type { CommonFieldDef } from '@/core/dcr/fields/types';
import { getPicklistOptions } from '@/core/dcr/picklists';

import { useDcrFormContext } from '../../_context/DcrFormContext';
import { useFieldLabel } from '../../_hooks/useFieldLabel';
import { hasValue } from '../../_utils/hasValue';

type Props = {
	name: string;
	def: CommonFieldDef;
};

export const usePicklistFieldOptions = (def: CommonFieldDef) => {
	const { picklists } = useDcrFormContext();

	return useMemo(() => {
		if (!def.picklist) return [];

		const options = getPicklistOptions(picklists, def.picklist);
		const allowed = def.allowedOptionIds;

		return allowed ? options.filter(o => allowed.includes(o.id)) : options;
	}, [def.allowedOptionIds, def.picklist, picklists]);
};

export const PicklistInput = ({ name, def }: Props) => {
	const { control } = useFormContext();
	const fieldLabel = useFieldLabel();
	const options = usePicklistFieldOptions(def);

	return (
		<Controller
			name={name}
			control={control}
			render={({ field, fieldState }) => (
				<FormAutocompleteField
					id={name}
					name={field.name}
					label={fieldLabel(def)}
					options={options}
					defaultValue={(field.value as string | undefined) ?? ''}
					isRequired={def.required}
					isReadOnly={def.readOnly}
					disabled={options.length === 0}
					indicateError={!!fieldState.error}
					isDirty={hasValue(field.value)}
					handleOnDropdownItemSelect={item => field.onChange(item?.id ?? '')}
				/>
			)}
		/>
	);
};
